import type { ReactNode } from "react"

import { tv, type VariantProps } from "@/lib/utils/tv"

// The merchandising chip — the on-image vocabulary that rides on packshots.
// Everything is a rounded-full pill that stays legible over
// any photo; tone carries the meaning. `iconOnly` collapses it to a circular
// marker (the free-shipping dot) — pass an aria-label when there's no text.
const chip = tv({
  base: "inline-flex w-fit items-center gap-50 rounded-full whitespace-nowrap",
  variants: {
    size: {
      sm: "font-display text-micro font-semibold tracking-wide",
      md: "font-display text-caption font-semibold tracking-wide",
      detail: "font-text text-caption-lg leading-4.5 font-normal @card:text-body-sm @card:leading-4.75",
      // The inline form beside the payment plan's amount.
      little: "font-text text-little @3xl:text-little-lg",
    },
    tone: {
      nointerest: "bg-brand-muted text-fg-brand",
      new: "bg-brand text-fg-inverted uppercase",
      // Free shipping — a lime tint with olive ink (surface-freeship / fg-freeship).
      freeship: "bg-surface-freeship text-fg-freeship",
      // Secured plans' "Más tiempo" — a green tint with olive ink (surface-moretime / fg-moretime).
      moretime: "bg-surface-moretime text-fg-moretime",
      sale: "bg-surface-danger-subtle text-fg-danger-deep",
      vip: "bg-vip-fill text-gold-dark",
      lightning: "bg-brand font-extrabold text-nelo-black-soft",
    },
    iconOnly: { true: "justify-center", false: "" },
  },
  compoundVariants: [
    { iconOnly: false, size: "sm", class: "px-200 py-100" },
    { iconOnly: false, size: "md", class: "px-250 py-100" },
    { iconOnly: false, size: "detail", class: "px-150" },
    { iconOnly: false, size: "little", class: "px-150 py-px" },
    // The PDP's "Sin intereses" and "Más tiempo" are 4px-radius tags, not stadiums; free shipping sets on a 19px line.
    { tone: ["nointerest", "moretime"], size: ["detail", "little"], class: "rounded-2x" },
    { tone: "freeship", size: "detail", class: "leading-4.75" },
    { iconOnly: true, size: "sm", class: "size-5" },
    { iconOnly: true, size: "md", class: "size-6" },
    { iconOnly: true, size: "detail", class: "size-5" },
  ],
  defaultVariants: { tone: "nointerest", size: "md", iconOnly: false },
})

interface ChipProps extends VariantProps<typeof chip> {
  className?: string
  children: ReactNode
  "aria-label"?: string
  title?: string
}

export const Chip = ({ tone, size, iconOnly, className, children, ...rest }: ChipProps) => (
  <span className={chip({ tone, size, iconOnly, className })} {...rest}>
    {children}
  </span>
)
